import React, { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Check, X, RotateCcw } from 'lucide-react'
import { Button } from '../ui/Button'
import { useToast } from '../ui/Toast'
import {
  blockerService,
  type Blocker,
  type BlockerItemType,
  type BlockerStatus,
} from '../../services/blockerService'

interface BlockersSectionProps {
  itemType: BlockerItemType
  itemId: number | string
  /** Seed blockers to avoid an initial fetch (e.g. already loaded by host). */
  initialBlockers?: Blocker[]
  className?: string
  /** Notify host when the blocker set changes (e.g. to refresh a count). */
  onChange?: (blockers: Blocker[]) => void
}

const SEVERITY_LABEL: Record<string, string> = {
  low: 'low',
  medium: 'medium',
  high: 'high',
}

function isOpen(b: Blocker): boolean {
  return (b.status ?? 'active') === 'active'
}

/**
 * "What's getting in the way" — renders the demoted-roadblock blocker
 * annotations recorded on a blockable item and lets the user resolve,
 * dismiss, or reopen them. See newage-horton/docs/blocker-model.md.
 */
export const BlockersSection: React.FC<BlockersSectionProps> = ({
  itemType,
  itemId,
  initialBlockers,
  className,
  onChange,
}) => {
  const toast = useToast()
  const [blockers, setBlockers] = useState<Blocker[]>(initialBlockers ?? [])
  const [loading, setLoading] = useState(!initialBlockers)
  const [pendingId, setPendingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const data = await blockerService.getBlockers(itemType, itemId)
      setBlockers(data)
      onChange?.(data)
    } finally {
      setLoading(false)
    }
    // onChange intentionally omitted to avoid refetch loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemType, itemId])

  useEffect(() => {
    if (!initialBlockers) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemType, itemId])

  const setStatus = async (b: Blocker, status: BlockerStatus) => {
    if (!b.id) return
    setPendingId(b.id)
    // optimistic update
    const prev = blockers
    const optimistic = blockers.map((x) =>
      x.id === b.id ? { ...x, status } : x,
    )
    setBlockers(optimistic)
    try {
      const updated = await blockerService.updateBlockerStatus(
        itemType,
        itemId,
        b.id,
        status,
      )
      setBlockers(updated)
      onChange?.(updated)
      const verb =
        status === 'resolved'
          ? 'Marked as worked through'
          : status === 'dismissed'
            ? 'Set aside'
            : 'Reopened'
      toast.success(verb)
    } catch (err) {
      setBlockers(prev)
      toast.error(
        'Could not update',
        err instanceof Error ? err.message : 'Please try again.',
      )
    } finally {
      setPendingId(null)
    }
  }


  if (loading) return null
  if (!blockers || blockers.length === 0) return null

  const open = blockers.filter(isOpen)
  const closed = blockers.filter((b) => !isOpen(b))

  return (
    <div className={className}>
      {/* The one section heading every item page uses (2026-09-12); this was
          the only section on the task page in a different voice. */}
      <div className="flex items-center gap-1.5 mb-2">
        <h3 className="flex items-center gap-1.5 text-app-meta font-bold uppercase tracking-widest text-secondary">
          <AlertTriangle className="h-3.5 w-3.5 text-warn flex-shrink-0" />
          What's getting in the way
        </h3>
        {open.length > 0 && (
          <span className="text-app-micro font-medium text-muted">· {open.length}</span>
        )}
      </div>

      <ul className="space-y-2">
        {open.map((b) => (
          <li
            key={b.id}
            className="rounded-lg border border-tint-warn-edge bg-tint-warn-wash px-3 py-2"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-primary break-words">
                  {b.reason || b.title || 'Something is getting in the way'}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {b.type && (
                    <span className="text-app-micro uppercase tracking-wide text-secondary">
                      {b.type}
                    </span>
                  )}
                  {b.severity && SEVERITY_LABEL[String(b.severity)] && (
                    <span className="text-app-micro text-warn">
                      {SEVERITY_LABEL[String(b.severity)]} impact
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pendingId === b.id}
                  onClick={() => setStatus(b, 'resolved')}
                  title="I've worked through this"
                >
                  <Check className="w-3.5 h-3.5 mr-1" />
                  Worked through
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={pendingId === b.id}
                  onClick={() => setStatus(b, 'dismissed')}
                  title="Not relevant right now"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {closed.length > 0 && (
        <ul className="mt-2 space-y-1">
          {closed.map((b) => (
            <li
              key={b.id}
              className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs text-secondary"
            >
              <span className="min-w-0 truncate line-through opacity-80">
                {b.reason || b.title || 'Blocker'}
                <span className="ml-2 not-italic no-underline">
                  {b.status === 'resolved' ? 'worked through' : 'set aside'}
                </span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={pendingId === b.id}
                onClick={() => setStatus(b, 'active')}
                title="Reopen"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default BlockersSection
